import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { useTranslation } from "react-i18next";
import AuthContext from "../../../contexts/AuthContext";

// Map the backend `reason` to a copy bundle. badToken -> expired (the printed
// QR was rotated); disabled -> not open; notMixer/notFound -> generic.
const bundleFor = (reason) => {
  if (reason === "badToken") {
    return { kind: "expired", kicker: "walkup.expired.kicker", title: "walkup.expired.title", body: "walkup.expired.body", hint: "walkup.expired.hint" };
  }
  if (reason === "notEligible") {
    return { kind: "noteligible", title: "walkup.noteligible.title", body: "walkup.noteligible.body" };
  }
  if (reason === "disabled") {
    return { kind: "notopen", title: "walkup.notopen.title", body: "walkup.notopen.body" };
  }
  return { kind: "notfound", title: "walkup.notfound.title", body: "walkup.notfound.body" };
};

const WalkUpMessage = ({ reason, event }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { appStyle, signedIn } = React.useContext(AuthContext);
  const logo = appStyle?.logo;
  const b = bundleFor(reason);
  const isExpired = b.kind === "expired";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "#fff",
        px: 4,
        py: 6,
        maxWidth: 440,
        mx: "auto",
        textAlign: "center",
      }}
    >
      {logo && (
        <Box
          component="img"
          src={logo}
          alt=""
          sx={{ height: 120, mb: 3, opacity: 0.55, filter: "grayscale(.4)" }}
        />
      )}

      <Box
        sx={{
          width: 96,
          height: 96,
          borderRadius: "22px",
          display: "grid",
          placeItems: "center",
          mb: 3,
          background: "repeating-linear-gradient(45deg, #F1ECE8, #F1ECE8 8px, #E9E2DC 8px, #E9E2DC 16px)",
          border: "1px solid #E4DCD6",
        }}
      >
        {isExpired ? (
          <AccessTimeIcon sx={{ fontSize: 42, color: "#9A8E87" }} />
        ) : (
          <InfoOutlinedIcon sx={{ fontSize: 42, color: "#9A8E87" }} />
        )}
      </Box>

      {b.kicker && (
        <Typography sx={{ font: "700 11px Bitter, serif", letterSpacing: ".14em", textTransform: "uppercase", color: "#A1968F", mb: 1 }}>
          {t(b.kicker)}
        </Typography>
      )}
      <Typography component="h1" sx={{ font: "800 26px/1.15 Bitter, serif", color: "#211B19", letterSpacing: "-.015em", textWrap: "balance" }}>
        {t(b.title)}
      </Typography>
      <Typography sx={{ font: "500 15px/1.6 Bitter, serif", color: "#6E635E", mt: 1.5, maxWidth: 360 }}>
        {t(b.body)}
      </Typography>

      {isExpired && (
        <Box
          sx={{
            mt: 3,
            display: "flex",
            alignItems: "center",
            gap: 1.2,
            background: "#FBF6F2",
            border: "1px solid #EFE6E0",
            borderRadius: "14px",
            padding: "12px 16px",
          }}
        >
          <PersonOutlineIcon sx={{ color: theme.palette.primary.main }} />
          <Typography sx={{ font: "500 13.5px/1.45 Bitter, serif", color: "#4A423E", textAlign: "left" }}>
            {t("walkup.expired.hint")}
          </Typography>
        </Box>
      )}

      <Button
        variant="outlined"
        onClick={() => navigate("/")}
        sx={{ mt: 4, borderRadius: "12px", textTransform: "none", px: 4 }}
      >
        {signedIn ? t("nav.home.button") : t("auth.backtologin.btn")}
      </Button>

      {event?.name && (
        <Typography sx={{ font: "500 12.5px Bitter, serif", color: "#A1968F", mt: 3 }}>
          {event.name}
          {event.venueName ? ` · ${event.venueName}` : ""}
        </Typography>
      )}
    </Box>
  );
};

export default WalkUpMessage;
